import { Button } from "@rosc/ui/components/button";
import { Card, CardContent } from "@rosc/ui/components/card";
import { Skeleton } from "@rosc/ui/components/skeleton";
import { Link, Outlet, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  isAdminPath,
  requestAdminStepUp,
  useAdminAccess,
} from "@/lib/admin-access";
import { clearAdminQueries, lockAdmin } from "@/lib/admin-queries";
import {
  NativeAuthRequestError,
  type NativeSession,
  nativeAuthRequest,
  nativeSessionSchema,
} from "@/lib/native-auth";
import { queryClient } from "@/lib/query-client";
import { useAuthStore } from "@/store/auth.store";
import { AdminStepUp } from "./AdminStepUp";
import { AdminStepUpBanner } from "./AdminStepUpBanner";
import { useAdminIdle } from "./useAdminIdle";

export function beforeAdminLoad({
  cause,
  preload,
}: {
  cause: string;
  preload: boolean;
}) {
  // The parent route enters once, even when navigating among its child pages.
  if (!preload && cause === "enter") requestAdminStepUp();
}

type Access =
  | { kind: "loading" }
  | { kind: "forbidden" }
  | { kind: "error" }
  | { kind: "ready"; session: NativeSession; deadline: number };

export function AdminLayout() {
  const router = useRouter();
  const uid = useAuthStore((state) => state.user?.uid);
  const needsConfirmation = useAdminAccess((state) => state.needsConfirmation);
  const [access, setAccess] = useState<Access>({ kind: "loading" });
  const [lockError, setLockError] = useState<string | null>(null);
  const [locking, setLocking] = useState(false);
  const leavingLocked = useRef(false);
  const confirmation = useRef<{
    generation: number;
    trigger: Parameters<typeof lockAdmin>[0];
  }>({ generation: 0, trigger: "navigate" });
  const invalidateConfirmation = useCallback(
    (trigger: Parameters<typeof lockAdmin>[0]) => {
      confirmation.current = {
        generation: confirmation.current.generation + 1,
        trigger,
      };
      requestAdminStepUp();
      clearAdminQueries();
    },
    [],
  );
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const unsubscribe = useAuthStore.subscribe((state, previous) => {
      if (state.user?.uid !== previous.user?.uid)
        invalidateConfirmation("navigate");
    });
    return () => {
      confirmation.current.generation++;
      unsubscribe();
    };
  }, [invalidateConfirmation]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: Reload access on account changes and explicit retries.
  useEffect(() => {
    const controller = new AbortController();
    setAccess({ kind: "loading" });
    void nativeAuthRequest("session", undefined, { signal: controller.signal })
      .then((raw) => {
        const session = nativeSessionSchema.parse(raw);
        if (controller.signal.aborted) return;
        setAccess(
          session.adminStepUpExpiresIn === undefined
            ? { kind: "forbidden" }
            : {
                kind: "ready",
                session,
                deadline: Date.now() + session.adminStepUpExpiresIn * 1000,
              },
        );
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted)
          setAccess({
            kind:
              error instanceof NativeAuthRequestError &&
              error.code === "FORBIDDEN"
                ? "forbidden"
                : "error",
          });
      });
    return () => controller.abort();
  }, [uid, attempt]);
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event.type !== "updated" || event.query.queryKey[0] !== "admin")
          return;
        const error = event.query.state.error;
        if (error && "code" in error) {
          if (error.code === "FORBIDDEN") setAccess({ kind: "forbidden" });
          if (error.code === "ADMIN_STEP_UP_REQUIRED") requestAdminStepUp();
        }
      }),
    [],
  );
  useEffect(
    () =>
      router.subscribe("onBeforeNavigate", ({ fromLocation, toLocation }) => {
        if (
          !fromLocation ||
          !isAdminPath(fromLocation.pathname) ||
          isAdminPath(toLocation.pathname)
        )
          return;
        invalidateConfirmation("navigate");
        if (!leavingLocked.current) void lockAdmin("navigate").catch(() => {});
      }),
    [router, invalidateConfirmation],
  );
  useEffect(() => {
    const close = () => {
      invalidateConfirmation("close");
      void lockAdmin("close").catch(() => {});
    };
    window.addEventListener("pagehide", close);
    return () => window.removeEventListener("pagehide", close);
  }, [invalidateConfirmation]);
  const leave = async (trigger: "lock" | "idle") => {
    if (leavingLocked.current) return;
    leavingLocked.current = true;
    setLocking(true);
    setLockError(null);
    invalidateConfirmation(trigger);
    try {
      await lockAdmin(trigger);
      await router.navigate({ to: "/" });
    } catch {
      setLockError("Unable to confirm admin lock. Try again.");
      if (trigger === "idle") await router.navigate({ to: "/" });
    } finally {
      leavingLocked.current = false;
      setLocking(false);
    }
  };
  useAdminIdle(() => void leave("idle"));
  return (
    <main className="mx-auto min-h-screen max-w-6xl px-4 py-6 sm:px-8">
      <header className="mb-6 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Admin</h1>
        <Button
          variant="outline"
          onClick={() => void router.navigate({ to: "/" })}
        >
          Home
        </Button>
      </header>
      {access.kind === "loading" && (
        <Skeleton className="h-24 w-full" aria-label="Loading admin access" />
      )}
      {access.kind === "error" && (
        <div role="alert">
          Unable to load admin access.{" "}
          <Button onClick={() => setAttempt((value) => value + 1)}>
            Try again
          </Button>
        </div>
      )}
      {access.kind === "forbidden" && <p role="alert">Not authorized</p>}
      {access.kind === "ready" && (
        <>
          <AdminStepUpBanner
            deadline={access.deadline}
            onConfirm={requestAdminStepUp}
            onLock={() => void leave("lock")}
          />
          {needsConfirmation ? (
            <AdminStepUp
              locking={locking}
              lockError={lockError}
              session={access.session}
              beginConfirmation={() => confirmation.current.generation}
              onForbidden={() => setAccess({ kind: "forbidden" })}
              onLeave={() => void leave("lock")}
              onConfirmed={(seconds, generation) => {
                // Revocation invalidates pending proofs even when bfcache keeps this page mounted.
                if (generation !== confirmation.current.generation) {
                  requestAdminStepUp();
                  clearAdminQueries();
                  void lockAdmin(confirmation.current.trigger).catch(() => {});
                  return;
                }
                setAccess({ ...access, deadline: Date.now() + seconds * 1000 });
                useAdminAccess.setState({ needsConfirmation: false });
                void queryClient.invalidateQueries({ queryKey: ["admin"] });
              }}
            />
          ) : (
            <Card className="mt-6">
              <CardContent>
                <nav aria-label="Admin pages" className="mb-6 flex gap-4">
                  <Link
                    to="/admin"
                    activeOptions={{ exact: true }}
                    activeProps={{ className: "font-semibold underline" }}
                  >
                    Overview
                  </Link>
                  <Link
                    to="/admin/users"
                    activeProps={{ className: "font-semibold underline" }}
                  >
                    Users
                  </Link>
                  <Link
                    to="/admin/audit"
                    activeProps={{ className: "font-semibold underline" }}
                  >
                    Audit log
                  </Link>
                </nav>
                <Outlet />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </main>
  );
}
